import { buildSessionScopedStorageKey } from './sessionScope';

/**
 * Build a browser-storage key for flight-specific drafts or snapshots.
 *
 * Flight primary keys repeat across tenant schemas, so a bare key such as
 * `gendec_22` is not an identity boundary. Refuse storage entirely until an
 * authenticated tenant/company/user scope is available.
 */
export function buildFlightScopedStorageKey(prefix, flightId, scope) {
  const base = buildSessionScopedStorageKey(prefix, scope);
  const normalizedFlightId = String(flightId ?? '').trim();
  if (!base || !normalizedFlightId) return null;
  return `${base}:${encodeURIComponent(normalizedFlightId)}`;
}

function browserStorage(kind, explicitStorage) {
  if (explicitStorage !== undefined) return explicitStorage;
  try {
    if (typeof window === 'undefined') return null;
    return kind === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function removeMatchingKeys(storage, predicate) {
  if (!storage) return [];
  const keys = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && predicate(key)) keys.push(key);
    }
  } catch {
    return [];
  }
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      // Continue removing the remaining legacy payloads.
    }
  }
  return keys;
}

/**
 * One-way cleanup for the former ID-only keys. They can contain a complete
 * checklist or GENDEC passenger/passport draft and cannot be assigned safely
 * to a tenant after the fact, so migration would risk preserving the leak.
 */
export function clearLegacyUnscopedFlightStorage(options = {}) {
  const persistentStorage = browserStorage('local', options.localStorage);
  const transientStorage = browserStorage('session', options.sessionStorage);
  return {
    local: removeMatchingKeys(
      persistentStorage,
      (key) => /^gendec_[^:]+$/.test(String(key))
    ),
    session: removeMatchingKeys(
      transientStorage,
      (key) => /^aviatto_checklist_flight_[^:]+$/.test(String(key))
    ),
  };
}
